import React, { useEffect } from 'react';

// Truncate description to ~155 characters
const truncateDescription = (text, maxLength = 155) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3).trim() + '...';
};

/**
 * SEO Meta Component for dynamic page titles, descriptions, and JSON-LD.
 *
 * NOTE: We do NOT use react-helmet-async — it's incompatible with React 19
 * (silently drops <meta>, <link>, and <script> children).
 *
 * Strategy:
 * - <meta> and <link rel="canonical"> are rendered in JSX. React 19
 *   automatically hoists these into <head> with deduplication.
 * - <title> is set via an effect on `document.title` to avoid duplicating
 *   the static <title> in index.html.
 * - JSON-LD <script> is injected via an effect because React 19 does not
 *   reliably hoist inline scripts.
 */
const SEOHead = ({
  title,
  description,
  canonicalUrl,
  type = 'website',
  jsonLd = null,
  image = null,
}) => {
  const truncatedDesc = truncateDescription(description);
  const pageUrl =
    canonicalUrl ||
    (typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : '');
  const ogImage = image || 'https://greatwine.deals/og-image.png';
  const ogType = type === 'product' ? 'product' : 'website';

  // Set document title without leaving a second <title> in <head>.
  useEffect(() => {
    if (!title) return;
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);

  // Inject JSON-LD as a <script> in <head>. React 19 doesn't reliably hoist
  // inline scripts, so we manage this one imperatively.
  useEffect(() => {
    if (!jsonLd) return undefined;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'page');
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [jsonLd]);

  return (
    <>
      <meta name="description" content={truncatedDesc} />
      <link rel="canonical" href={pageUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={truncatedDesc} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={truncatedDesc} />
      <meta name="twitter:image" content={ogImage} />
    </>
  );
};

export default SEOHead;
